import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { BREAKPOINTS_MEDIUM, BREAKPOINTS_SMALL, MAX_DATE_RANGE_IN_MONTHS, US_CENTER_LAT_LNG } from '@app-core/constants/constants';
import { AssetAutoCompleteResponseObject } from '@app-core/models/core.model';
import { DataService } from '@app-core/services/data/data.service';
import { DateService } from '@app-core/services/date/date.service';
import { GoogleTagManagerService } from '@app-core/services/google-tag-manager/google-tag-manager.service';
import { FIND_TRIP_TABLE_COLUMNS } from '@app-dvr/constants/dvr.constants';
import { TripsService } from '@app-trips/services/trips.service';
import { CLIENT_CONFIG } from '@config/config';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError, finalize, takeUntil } from 'rxjs/operators';
import { MixPanelService } from '@app-core/services/mixpanel/mix-panel.service';
@Component({
  selector: 'app-find-trip',
  templateUrl: './find-trip.component.html',
  styleUrls: ['./find-trip.component.scss'],
})
export class FindTripComponent implements OnInit, AfterViewInit, OnDestroy {
  private ngUnsubscribe: Subject<void> = new Subject<void>();

  @ViewChild('paginator', { static: true })
  public paginator: MatPaginator;
  public fleetId: string;
  public driverList = [];
  public loader = false;
  public form = null;
  public isTouchDevice = false;
  public tablePageSize = 5;
  public displayedColumns: string[] = FIND_TRIP_TABLE_COLUMNS;
  public clientConfig = CLIENT_CONFIG;
  public dataSource = new MatTableDataSource<any>();
  public minStartDate: string;
  public maxEndDate: string;
  public selectedFilterType = 'driverFilter';
  public dvrAvailableLoader = true;
  public isFormSubmitted = false;
  public filterTypeList = [
    {
      label: 'Driver',
      value: 'driverFilter',
    },
    {
      label: 'Asset',
      value: 'assetFilter',
    },
  ];
  public customOptions = {
    showLargeInput: false,
    showAdditionalDisplayProp: true,
    additionalDisplayPropKey: 'driverName',
  };

  constructor(
    private dialogRef: MatDialogRef<FindTripComponent>,
    private fb: FormBuilder,
    private breakpointObserver: BreakpointObserver,
    private tripsService: TripsService,
    private dataService: DataService,
    private dateService: DateService,
    private router: Router,
    private gtmService: GoogleTagManagerService,
    private mixpanelService: MixPanelService
  ) {}

  public ngOnInit() {
    this.selectedFilterType = this.clientConfig?.isAssetCentric ? 'assetFilter' : 'driverFilter';
    this.form = this.fb.group({
      startDate: [new Date(), [Validators.required]],
      ...(this.clientConfig?.isAssetCentric
        ? { filterType: ['assetFilter', Validators.required] }
        : { filterType: ['driverFilter', Validators.required] }),
      driverId: [''],
      asset: [],
      includeInsignificantTrips: [true],
    });

    this.maxEndDate = this.maxEndDate = new Date().toISOString();
    this.minStartDate = this.dateService.subtractMonths(this.maxEndDate, MAX_DATE_RANGE_IN_MONTHS);

    this.displayedColumns = FIND_TRIP_TABLE_COLUMNS.filter((x: any) => x !== 'driver');

    this.form.get('startDate').valueChanges.subscribe(() => {
      this.gtmService.changeVideoRequestSearchTripDateFilter(this.fleetId);
      this.mixpanelService.track('Changes Video Request Search Trip Date', {
        'Fleet Id': this.fleetId,
      });
    });
    this.form.get('filterType').valueChanges.subscribe((value: any) => {
      this.selectedFilterType = value;
      this.gtmService.changeVideoRequestSearchTripFilterType(this.selectedFilterType);
      this.mixpanelService.track('Changes Video Request Search Trip Filter', {
        'Filter Type': this.selectedFilterType,
      });
    });

    this.breakpointObserver.observe([...BREAKPOINTS_SMALL, ...BREAKPOINTS_MEDIUM]).subscribe((state: BreakpointState) => {
      this.isTouchDevice = state.matches;
    });

    this.dataService._currentFleet.subscribe((value: string) => {
      if (value) {
        this.fleetId = value;
      }
    });
  }

  public ngAfterViewInit() {
    this.paginator.page.pipe(takeUntil(this.ngUnsubscribe)).subscribe((event: PageEvent) => {
      this.gtmService.changeVideoRequestSearchTripListPageChange(event);
    });
  }

  public driverChanged(driverId = '') {
    this.gtmService.changeVideoRequestSearchTripDriverIdFilter(driverId);
    this.mixpanelService.track('Changes Video Request Search Trip Driver Id Filter', {
      'Driver Id': driverId,
    });
    this.form.patchValue({ driverId });
  }

  public assetChanged(asset: AssetAutoCompleteResponseObject) {
    this.gtmService.changeVideoRequestSearchTripAssetIdFilter(asset?.assetId);
    this.mixpanelService.track('Changes Video Request Search Trip Asset Id Filter', {
      'Asset Id': asset?.assetId,
    });
    this.form.patchValue({ asset });
  }

  public navigateToVideoRequest(trip: any) {
    this.dialogRef.close();
    const {
      driverId,
      tripId,
      firstLocation: { latitude = US_CENTER_LAT_LNG.latitude, longitude = US_CENTER_LAT_LNG.longitude } = {},
    } = trip || {};
    this.gtmService.gotoRequestVideoPageFromVideoRequestPage(trip.asset.assetId, driverId);
    this.router.navigate(['/request-video'], {
      queryParams: {
        driverId,
        tripId,
        latitude,
        longitude,
      },
    });
  }

  public navigateToTripDetails(trip: any) {
    this.dialogRef.close();
    const {
      driverId,
      tripId,
      firstLocation: { latitude = US_CENTER_LAT_LNG.latitude, longitude = US_CENTER_LAT_LNG.longitude } = {},
    } = trip || {};
    this.gtmService.gotoTripDetailsFromVideoRequestSearchTripList(trip.asset.assetId, driverId);
    this.router.navigate(['/trip-details'], {
      queryParams: {
        driverId,
        tripId,
        latitude,
        longitude,
      },
    });
  }

  public onSearch() {
    this.loader = true;
    this.isFormSubmitted = true;
    this.dvrAvailableLoader = true;
    this.dataSource.data = new Array(this.tablePageSize).fill({});
    const { startDate, driverId, asset, includeInsignificantTrips } = this.form.value || {};
    const now = new Date(this.maxEndDate);
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const seconds = now.getSeconds();
    const milliSeconds = now.getMilliseconds();

    const from = this.dateService.toDaysStartISO(new Date(startDate.setHours(hours, minutes, seconds, milliSeconds)));
    const to = this.dateService.toDaysEndISOPlusOne(new Date(startDate.setHours(hours, minutes, seconds, milliSeconds)));

    const params = {
      fleetId: this.fleetId,
      startDate: from,
      endDate: to,
      driverId: driverId || null,
      assetId: asset?.assetId,
      includeInsignificantTrips,
      offset: 0,
      limit: 500,
      sort: 'desc',
      sortBy: 'startTime',
    };

    const API =
      this.selectedFilterType === 'driverFilter' ? this.tripsService.getDriverTripList(params) : this.tripsService.getAssetTripList(params);

    API.pipe(
      finalize(() => {
        this.loader = false;
      }),
      takeUntil(this.ngUnsubscribe)
    ).subscribe(
      ({ data = {} }) => {
        const { trips = [] } = data || {};
        const updatedTrips = trips.map((x: any) => {
          return { ...x, isDvrAvailable: true };
        });
        this.dataSource.data = updatedTrips;
        this.dataSource.paginator = this.paginator;
        this.checkDvrAvaibility(updatedTrips);
      },
      () => {
        this.dataSource.data = [];
      }
    );
  }

  private checkDvrAvaibility(trips: any[]) {
    this.dvrAvailableLoader = true;
    const APIS = (trips || []).map((x: any) => {
      const { device = {} } = x || {};
      const { deviceId = null } = device || {};
      const dateRange = this.dateService.getDateRangeInISO(30);
      const { from, to } = dateRange;
      const params = {
        startDate: from,
        endDate: to,
        deviceId: deviceId,
        limit: 3,
        eventTypes: ['Device-Storage', 'TripStarted', 'TripEnded'],
      };
      return this.tripsService.checkDvrAvailibility(params);
    });

    forkJoin(APIS)
      .pipe(
        finalize(() => {
          this.dvrAvailableLoader = false;
        }),
        takeUntil(this.ngUnsubscribe),
        catchError(() => of(undefined))
      )
      .subscribe(
        (res: any[]) => {
          const updatedTrips = trips.map((x, index) => {
            const { events = [] } = res[index] || {};
            const { startTimeUTC = '' } = x;
            const { oldestDvrTimestamp = null } =
              events.length &&
              events
                .filter((x: any) => x !== null && x.hasOwnProperty('oldestDvrTimestamp'))
                .reduce((a: any, b: any) => (a.timestampUTC > b.timestampUTC ? a : b), {});
            if (oldestDvrTimestamp) {
              if (new Date(oldestDvrTimestamp).getTime() < new Date(startTimeUTC).getTime()) {
                return {
                  ...x,
                  isDvrAvailable: true,
                };
              } else {
                return {
                  ...x,
                  isDvrAvailable: false,
                };
              }
            } else {
              return {
                ...x,
                isDvrAvailable: true,
              };
            }
          });

          this.dataSource.data = updatedTrips;
          this.dataSource.paginator = this.paginator;
        },
        () => {}
      );
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
